import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./DriverPortalOperationsPager.css";

const isComplete = (operation, kind) => kind === "loading"
  ? operation.isEffectivelyCompleted
  : operation.isConfirmed;

function initialKey(operations, kind, focusedDeliveryId) {
  return (operations.find((operation) => focusedDeliveryId && operation.deliveryId === focusedDeliveryId)
    || operations.find((operation) => !isComplete(operation, kind))
    || operations[0])?.key;
}

export default function DriverPortalOperationsPager({
  operations,
  kind,
  focusedDeliveryId = "",
  disabled = false,
  children,
}) {
  const [selectedKey, setSelectedKey] = React.useState(() => initialKey(operations, kind, focusedDeliveryId));
  const [navigation, setNavigation] = React.useState(null);
  const viewportRef = React.useRef(null);
  const cardRef = React.useRef(null);
  const outgoingRef = React.useRef(null);
  const swipe = React.useRef(null);
  const previousFocus = React.useRef(focusedDeliveryId);
  const contentId = React.useId();
  const labelId = React.useId();
  const selectedIndex = operations.findIndex((operation) => operation.key === selectedKey);
  const activeIndex = selectedIndex >= 0 ? selectedIndex
    : operations.findIndex((operation) => operation.key === initialKey(operations, kind));
  const active = operations[activeIndex];
  const [attentionKey, setAttentionKey] = React.useState(null);
  const previousConfirmation = React.useRef({ key: active?.key, confirmed: Boolean(active?.isConfirmed) });
  const pendingAttention = React.useRef(false);
  const hasNext = activeIndex >= 0 && activeIndex < operations.length - 1;
  const canGoNext = !disabled && hasNext && isComplete(active, kind);

  React.useEffect(() => {
    const confirmed = Boolean(active?.isConfirmed);
    const previous = previousConfirmation.current;
    if (previous.key !== active?.key || !confirmed || !hasNext) {
      pendingAttention.current = false;
      setAttentionKey(null);
    } else if (!previous.confirmed) {
      pendingAttention.current = true;
    }
    previousConfirmation.current = { key: active?.key, confirmed };
    // Confirmation data can arrive before the request releases the controls.
    if (disabled) {
      setAttentionKey(null);
      return;
    }
    if (pendingAttention.current) {
      pendingAttention.current = false;
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) setAttentionKey(active.key);
    }
  }, [active?.key, active?.isConfirmed, disabled, hasNext]);

  React.useEffect(() => {
    // Keep identity through confirmations/reordering; only replace a removed operation.
    setSelectedKey(active?.key);
    swipe.current = null;
  }, [active?.key, disabled]);

  React.useEffect(() => {
    if (previousFocus.current === focusedDeliveryId) return;
    previousFocus.current = focusedDeliveryId;
    const target = operations.find((operation) => focusedDeliveryId && operation.deliveryId === focusedDeliveryId);
    if (target) setSelectedKey(target.key);
  }, [focusedDeliveryId, operations]);

  React.useLayoutEffect(() => {
    if (!navigation || navigation.key !== active?.key) return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const finish = () => setNavigation(current => current === navigation ? null : current);
    if (media.matches || !cardRef.current?.animate) {
      finish();
      return;
    }
    const sign = navigation.direction === "next" ? 1 : -1;
    const timing = { duration: 420, easing: "cubic-bezier(0.22, 0.8, 0.2, 1)" };
    const incoming = cardRef.current.animate([
      { transform: `translateX(${sign * 100}%)`, opacity: 0.65 },
      { transform: "translateX(0)", opacity: 1 },
    ], timing);
    const outgoing = outgoingRef.current?.animate([
      { transform: "translateX(0) scale(1)", opacity: 1 },
      { transform: `translateX(${-sign * 28}%) scale(0.97)`, opacity: 0 },
    ], { ...timing, fill: "forwards" });
    const height = viewportRef.current.animate([
      { height: `${navigation.height}px` },
      { height: `${cardRef.current.offsetHeight}px` },
    ], timing);
    incoming.onfinish = finish;
    const onMotionChange = () => { if (media.matches) finish(); };
    media.addEventListener("change", onMotionChange);
    return () => {
      incoming.cancel(); outgoing?.cancel(); height.cancel();
      media.removeEventListener("change", onMotionChange);
    };
  }, [navigation, active?.key]);

  if (!active) return null;

  const multiple = operations.length > 1;
  const title = kind === "loading" ? "Chargement" : "Livraison";
  const completed = operations.filter((operation) => isComplete(operation, kind)).length;
  const status = active.isConfirmed ? (kind === "loading" ? "Confirmé" : "Confirmée")
    : isComplete(active, kind) ? "Soldé"
      : active.isBlocked ? "Bloquée" : active.isRevalidation ? "À revalider" : "À confirmer";
  const statusTone = isComplete(active, kind) ? "border-accent bg-accent text-white"
    : "border-slate-200 bg-slate-50 text-slate-600";

  function move(offset) {
    if (!disabled && (offset < 0 || canGoNext) && operations[activeIndex + offset]) {
      const key = operations[activeIndex + offset].key;
      setNavigation(window.matchMedia("(prefers-reduced-motion: reduce)").matches ? null : {
        key,
        direction: offset > 0 ? "next" : "previous",
        height: viewportRef.current?.getBoundingClientRect().height || 0,
        outgoing: children(active, activeIndex),
      });
      setSelectedKey(key);
    }
  }

  function beginSwipe(event) {
    swipe.current = null;
    if (!multiple || disabled || !event.isPrimary || event.pointerType === "mouse"
      || event.target.closest("input, textarea, select, button, a, [contenteditable], [role=button]")) return;
    swipe.current = { id: event.pointerId, x: event.clientX, y: event.clientY, key: active.key };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function finishSwipe(event) {
    const start = swipe.current;
    swipe.current = null;
    if (!start || start.id !== event.pointerId || start.key !== active.key) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.5) move(dx < 0 ? 1 : -1);
  }

  const buttonClassName = "inline-flex min-h-11 items-center justify-center gap-1 rounded-[4px] border px-3 text-sm font-semibold disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";

  return (
    <div className="driver-operation-pager mt-4 min-w-0 max-w-full">
      {multiple ? (
        <div className="mb-2 grid gap-2">
          <div aria-live="polite" aria-atomic="true">
            <div className="flex items-center justify-between gap-2">
              <p id={labelId} className="text-sm font-bold text-slate-900">{title} {activeIndex + 1} sur {operations.length}</p>
              <span className={`rounded-[4px] border px-2 py-0.5 text-xs font-semibold ${statusTone}`}>{status}</span>
            </div>
            <p className="mt-1 text-xs text-slate-600">Terminés : {completed}/{operations.length} · À faire : {operations.length - completed}</p>
          </div>
          <div className="grid grid-cols-2 gap-2" role="group" aria-label={`Navigation des ${kind === "loading" ? "chargements" : "livraisons"}`}>
            <button type="button" aria-label={`${title} ${kind === "loading" ? "précédent" : "précédente"}`} aria-controls={contentId}
              disabled={disabled || activeIndex === 0} onClick={() => move(-1)}
              className={`${buttonClassName} border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900`}>
              <ChevronLeft size={16} aria-hidden="true" /> Précédent
            </button>
            <button type="button" aria-label={`${title} ${kind === "loading" ? "suivant" : "suivante"}`} aria-controls={contentId}
              disabled={!canGoNext} onClick={() => move(1)}
              onAnimationEnd={() => setAttentionKey(null)}
              className={`${buttonClassName} ${canGoNext ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong" : "border-slate-200 bg-slate-50 text-slate-400"}${attentionKey === active.key && canGoNext ? " driver-operation-pager__next-attention" : ""}`}>
              Suivant <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}
      <div ref={viewportRef} id={contentId} className="driver-operation-pager__viewport" role="group" aria-labelledby={multiple ? labelId : undefined}
        aria-label={multiple ? undefined : title}
        style={multiple ? { touchAction: "pan-y pinch-zoom" } : undefined}
        onPointerDown={beginSwipe} onPointerUp={finishSwipe} onPointerCancel={() => { swipe.current = null; }}>
        {multiple && navigation?.key === active.key ? (
          <div ref={outgoingRef} className="driver-operation-pager__outgoing" aria-hidden="true" inert="">
            {navigation.outgoing}
          </div>
        ) : null}
        <div ref={cardRef} key={active.key} className="driver-operation-pager__card"
          data-slide-direction={multiple && navigation?.key === active.key ? navigation.direction : undefined}>
          {children(active, activeIndex)}
        </div>
      </div>
    </div>
  );
}
